import React from 'react';
import { Flame } from 'lucide-react';

interface BurningEffectProps {
  isBurning?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  intensity?: 'low' | 'medium' | 'high';
}

const BurningEffect: React.FC<BurningEffectProps> = ({
  isBurning = false,
  className = '',
  size = 'medium',
  intensity = 'medium'
}) => {
  if (!isBurning) return null;

  const sizeMap = {
    small: 'w-3 h-3',
    medium: 'w-4 h-4',
    large: 'w-6 h-6'
  };

  const particleCount = {
    low: 2,
    medium: 4,
    high: 6
  };

  const flameSize = sizeMap[size];
  const count = particleCount[intensity];

  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      {/* Main flame effect with flame-dance animation */}
      <div className="absolute inset-0 flame-dance">
        {/* Fire particles */}
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse"
            style={{
              left: `${15 + (i * 25) % 70}%`,
              top: `${20 + (i * 20) % 60}%`,
              animationDelay: `${i * 400}ms`,
              animationDuration: `${1500 + i * 300}ms`
            }}
          >
            <Flame 
              className={`text-red-500 ${flameSize}`}
              style={{
                filter: 'drop-shadow(0 0 3px rgba(239, 68, 68, 0.8))',
                transform: `rotate(${-15 + i * 10}deg)`
              }}
            />
          </div>
        ))}
        
        {/* Orange flame particles */}
        {Array.from({ length: Math.floor(count / 2) }).map((_, i) => (
          <div
            key={`orange-${i}`}
            className="absolute animate-ping"
            style={{
              left: `${25 + (i * 30) % 50}%`,
              top: `${10 + (i * 35) % 70}%`,
              animationDelay: `${i * 200 + 300}ms`,
              animationDuration: `${1200 + i * 400}ms`
            }}
          >
            <Flame 
              className={`text-orange-500 ${sizeMap.small}`}
              style={{
                filter: 'drop-shadow(0 0 2px rgba(249, 115, 22, 0.9))',
                transform: `rotate(${10 - i * 15}deg)`
              }}
            />
          </div>
        ))}
        
        {/* Hot embers */}
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={`ember-${i}`}
            className="absolute animate-bounce"
            style={{
              left: `${10 + (i * 80) % 80}%`,
              top: `${5 + (i * 90) % 90}%`,
              animationDelay: `${i * 500}ms`,
              animationDuration: `${2000 + i * 200}ms`
            }}
          >
            <div 
              className="w-1 h-1 bg-yellow-600 rounded-full"
              style={{
                filter: 'drop-shadow(0 0 2px rgba(202, 138, 4, 1))',
                boxShadow: '0 0 4px rgba(202, 138, 4, 0.6)'
              }} 
            />
          </div>
        ))}
      </div>
      
      {/* Fire aura glow effect */}
      <div 
        className="absolute inset-0 rounded-full opacity-30"
        style={{
          background: 'radial-gradient(circle, rgba(239, 68, 68, 0.3) 0%, rgba(249, 115, 22, 0.2) 40%, transparent 70%)',
          animation: 'pulse 2s ease-in-out infinite'
        }}
      />
    </div>
  );
};

export default BurningEffect;